import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useGDPByIndustry,
} from '@src/modules/performance/context/reports-api-hooks';
import Chart from 'react-apexcharts';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const lineColours = [
  'darkblue',
  'blue',
  'orangered',
  'seagreen',
  'magenta',
];

const informationSource = {
  title: 'Economic',
  content: [
    {
      title: 'GDP by industry',
      description:
  <Text fontSize="inherit" mb={2}>
    Displays top five industries for the selected network as at 30 June of the latest financial year reported by MBIE. All annual values are considered as estimates unless the latest year is showing as (Prov) in which case that year’s results are provisional.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Stats NZ
    {' '}
    <Link href="https://stats.govt.nz/tools/which-industries-contributed-to-your-regions-gdp" isExternal>Regional GDP</Link>
    {' '}
    and MBIE
    {' '}
    <Link href="https://www.mbie.govt.nz/business-and-employment/economic-development/regional-economic-development/modelled-territorial-authority-gross-domestic-product/" isExternal>Modelled Territorial Authority Gross Domestic Product (MTAGDP)</Link>
    {' '}
    and the
    {' '}
    <Link href="http://webrear.mbie.govt.nz/theme/gdp-by-industry/map/barchart/2017/new-zealand/" isExternal>MBIE Regional Economic Activity Tool.</Link>
  </Text>,
    },
  ],
};

function TerritorialActivityEconomic() {
  const { data: gdpByIndustry, isLoading } = useGDPByIndustry();
  const lockYear = gdpByIndustry?.[0]?.LockYear || null;
  // Extract distinct years from the data
  const distinctYears = [...new Set(gdpByIndustry?.map((item) => item.year))];
  const latestYear = distinctYears[distinctYears.length - 1];
  const isProvisional = gdpByIndustry?.some(
    (item) => item.year === latestYear && item.transform === 'provisional',
  );

  // Only mark the latest year if provisional
  const years = distinctYears.map((year) => (year === latestYear && isProvisional === true ? `${year} (Prov)` : year));

  // Filter and sort the data to get the top 5 categories for the latest year
  const top5Categories = gdpByIndustry?.filter((item) => item.year === latestYear)
    .sort((a, b) => b.GDP - a.GDP)
    .slice(0, 5)
    .map((item) => item.feature);

  // Chart data: create a line chart for each of the top 5 categories
  const chartData = top5Categories?.map((category, idx) => ({
    name: category,
    type: 'line',
    data: gdpByIndustry
      ?.filter((item) => item.feature === category)
      .map((item) => ({
        x: item.year === latestYear && isProvisional
          ? [item.year, '(Prov)']
          : item.year,
        y: item.GDP > 0 ? item.GDP / 1000000 : 0,
      })),
    color: CHART_COLOURS[lineColours[idx]],
  })) || [];

  // Calculate the min and max Y values for the chart
  const yValues = chartData.flatMap((series) => series.data.map((point) => point.y));
  const minY = Math.min(...yValues.filter((value) => value > 0)) * 0.9;
  const maxY = Math.max(...yValues) * 1.1;

  return (
    <ReportSection
      title="Economic"
      isLoading={isLoading || !gdpByIndustry}
      informationSource={informationSource}
      lockYear={lockYear}
    >
      <Chart
        options={
          {
            title: {
              text: ['GDP by top 5 industries', 'Annual estimates'],
            },
            chart: {
              animations: {
                enabled: false,
              },
            },
            xaxis: {
              categories: years,
              labels: {
                offsetY: 5,
                rotate: -45,
                rotateAlways: true,
              },
            },
            yaxis: [
              {
                forceNiceScale: false,
                min: minY,
                max: maxY,
                labels: {
                  formatter(y) {
                    return `$${y.toLocaleString(undefined, { maximumFractionDigits: 0 })}M`;
                  },
                },
              },
            ],
            grid: {
              padding: {
                left: 20,
                right: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            stroke: {
              curve: 'straight',
              width: distinctYears.length > 1 ? 2 : 0,
            },
            tooltip: {
              shared: true,
              intersect: false,
              y: {
                formatter(value) {
                  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
                },
              },
            },
            legend: {
              position: 'right',
              horizontalAlign: 'center', // centers vertically in the right column
              floating: false,
              offsetX: 0,
              offsetY: 0,
            },
            responsive: [
              {
                breakpoint: 900, // mobile and small tablets
                options: {
                  legend: {
                    position: 'bottom',
                    horizontalAlign: 'center',
                  },
                  chart: {
                    width: '100%',
                    height: '500',
                  },
                },
              },
            ],
          }
        }
        series={chartData}
        type="line"
        width="800"
        height="350"
      />
    </ReportSection>
  );
}

export default TerritorialActivityEconomic;
